/**
 * The six stage gates, as a vertical timeline.
 *
 * A package is not "62% done" in any way a director can act on. It is *at* a gate, and the
 * useful question is which one and how late. This answers both in the width of a sidebar.
 *
 * Variance is shown in days against the baseline, with a sign, because "31 d" and "-31 d"
 * are opposite news and a bare number is neither. State is carried by a mark as well as a
 * colour — a filled dot for done, a ring for the gate in progress, an outline for what has
 * not started — because this sits beside a status chip in the same sidebar and the two must
 * be distinguishable in a monochrome print of a board pack.
 */
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { api } from '@/api/client'
import { formatDate } from '@/lib/format'

type Gate = {
  gate_no: number
  name: string
  state: 'complete' | 'active' | 'pending'
  bl_finish: string | null
  actual_finish: string | null
  forecast_finish: string | null
  pct_complete: string | null
  variance_days: number | null
}

export function StatusTimeline({ packageId }: { packageId: string }) {
  const { t, i18n } = useTranslation()

  const gates = useQuery({
    queryKey: ['package', packageId, 'gates'],
    queryFn: ({ signal }) => api.get<Gate[]>(`/packages/${packageId}/gates`, signal),
    // Gates move when a schedule is imported, which is monthly.
    staleTime: 5 * 60_000,
  })

  // No schedule loaded for this package yet. An empty card would read as a failed request,
  // so the section simply does not appear — the sidebar above it still says everything the
  // screen knows.
  if (!gates.data || gates.data.length === 0) return null

  return (
    <section className="card card--pad" data-testid="timeline">
      <h2 className="card__caption">{t('package.timeline')}</h2>
      <ol className="timeline">
        {gates.data.map((gate) => (
          <li key={gate.gate_no} className="timeline__step" data-state={gate.state}>
            <span className="timeline__mark" aria-hidden="true" />
            <span className="timeline__body">
              <span className="timeline__name">{gate.name}</span>
              <span className="timeline__meta numeric">
                {formatDate(
                  gate.actual_finish ?? gate.forecast_finish ?? gate.bl_finish,
                  i18n.language,
                )}
                {gate.variance_days !== null && gate.variance_days !== 0 ? (
                  <span
                    className="timeline__variance"
                    data-late={gate.variance_days > 0 ? 'true' : undefined}
                  >
                    {gate.variance_days > 0 ? '+' : ''}
                    {t('package.days', { count: gate.variance_days })}
                  </span>
                ) : null}
              </span>
            </span>
            <span className="visually-hidden">{t(`package.gateState.${gate.state}`)}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}
